<template>
<div class="nav-wrapper">
  <header class="nav-admin-header" v-if="token">
    <div class="nav-brand">
      <div class="brand-badge">
        <span class="brand-dot"></span>
        <span class="brand-title">myMenu</span>
        <span class="brand-role">POS Suite</span>
      </div>
    </div>

    <div class="nav-segmented-control" role="tablist">
      <button 
        type="button"
        role="tab"
        :aria-selected="page === 'SALA'"
        class="nav-segment-btn" 
        :class="{ active: page === 'SALA' }" 
        @click="getPage('SALA')"
      >
        <i class="fas fa-th-large me-2"></i>
        <span>{{ $t('sala') }}</span>
      </button>

      <button 
        type="button"
        role="tab"
        :aria-selected="page === 'GESTIONALE'"
        class="nav-segment-btn" 
        :class="{ active: page === 'GESTIONALE' }" 
        @click="getPage('GESTIONALE')"
      >
        <i class="fas fa-layer-group me-2"></i>
        <span>{{ $t('gestionale') }}</span>
      </button>
    </div>

    <div class="nav-actions">
      <div class="nav-lang-chips">
        <button 
          v-for="lang in ['it', 'en', 'es']" 
          :key="lang"
          type="button"
          class="nav-lang-btn" 
          :class="{ active: $i18n.locale === lang }"
          @click="changeLang(lang)"
        >
          {{ lang.toUpperCase() }}
        </button>
      </div>

      <button type="button" class="nav-logout-btn" @click="logout" :title="$t('logout')">
        <i class="fas fa-sign-out-alt me-1"></i>
        <span class="logout-label">{{ $t('logout') }}</span>
      </button>
    </div>
  </header>

  <main class="main-content-area">
    <transition name="fade-page" mode="out-in">
      <div class="main-sala" v-if="page === 'SALA'" key="sala">
        <Sala :token="token" />
      </div>
      <div class="main-gestionale" v-else-if="page === 'GESTIONALE'" key="gestionale">
        <Gestionale :token="token" />
      </div>
    </transition>
  </main>
</div>
</template>

<script>
import Sala from '@/components/Sala.vue'
import Gestionale from '@/components/Gestionale.vue'

export default {
  name: 'NavBar',
  components: {
    Sala,
    Gestionale
  },
  data: function () {
    return {
      token: '',
      page: 'GESTIONALE',
    }
  },
  methods: {
    getToken () {
      this.token = this.$route.params.token || localStorage.token;
    },
    getPage (page) {
      this.page = page;
      localStorage.setItem('last_admin_page', page);
    },
    changeLang (lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('lang', lang);
    },
    logout () {
      localStorage.removeItem('token');
      this.token = '';
      this.page = '';
      this.$router.push({ name: "Home" });
    }
  },
  beforeMount() {
    this.getToken();
    const savedPage = localStorage.getItem('last_admin_page');
    if (savedPage && (savedPage === 'SALA' || savedPage === 'GESTIONALE')) {
      this.page = savedPage;
    }
  },
}
</script>

<style scoped>
.nav-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
}

.nav-admin-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
}

.nav-brand {
  display: flex;
  align-items: center;
}

.brand-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--sci-bg-subtle, #f1f5f9);
  padding: 6px 12px;
  border-radius: 9999px;
  border: 1px solid var(--sci-border, #e2e8f0);
}

.brand-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--sci-emerald, #059669);
  box-shadow: 0 0 6px rgba(5, 150, 105, 0.6);
}

.brand-title {
  font-weight: 800;
  font-size: 15px;
  letter-spacing: -0.02em;
  color: var(--sci-text-main, #0f172a);
}

.brand-role {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--sci-primary, #4f46e5);
  letter-spacing: 0.05em;
  padding-left: 4px;
  border-left: 1px solid var(--sci-border, #cbd5e1);
}

.nav-segmented-control {
  display: flex;
  align-items: center;
  background: var(--sci-bg-subtle, #f1f5f9);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid var(--sci-border, #e2e8f0);
  gap: 4px;
}

.nav-segment-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 8px 20px;
  border: none;
  background: transparent;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
  user-select: none;
}

.nav-segment-btn:hover {
  color: var(--sci-text-main, #0f172a);
}

.nav-segment-btn.active {
  background: #ffffff;
  color: var(--sci-primary, #4f46e5);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-lang-chips {
  display: inline-flex;
  background: var(--sci-bg-subtle, #f1f5f9);
  padding: 3px;
  border-radius: 8px;
  border: 1px solid var(--sci-border, #e2e8f0);
}

.nav-lang-btn {
  border: none;
  background: transparent;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  color: var(--sci-text-muted, #64748b);
  cursor: pointer;
  transition: all 0.15s ease;
}

.nav-lang-btn:hover {
  color: var(--sci-text-main, #0f172a);
}

.nav-lang-btn.active {
  background: #ffffff;
  color: var(--sci-text-main, #0f172a);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.nav-logout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid var(--sci-border, #e2e8f0);
  background: #ffffff;
  color: var(--sci-rose, #e11d48);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-logout-btn:hover {
  background: var(--sci-rose-subtle, #fff1f2);
  border-color: var(--sci-rose-border, #fecdd3);
}

.main-content-area {
  flex: 1;
  padding: 0;
}

.main-sala, .main-gestionale {
  min-height: calc(100vh - 65px);
}

.fade-page-enter-active, .fade-page-leave-active {
  transition: opacity 0.2s cubic-bezier(0.22, 1, 0.36, 1), transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.fade-page-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.fade-page-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 768px) {
  .nav-admin-header {
    flex-wrap: wrap;
    gap: 12px;
    padding: 10px 14px;
  }
  .nav-brand {
    order: 1;
  }
  .nav-actions {
    order: 2;
  }
  .nav-segmented-control {
    order: 3;
    width: 100%;
    justify-content: center;
  }
  .nav-segment-btn {
    flex: 1;
  }
  .logout-label {
    display: none;
  }
}
</style>
